import React from 'react';
import { Box, BoxProps } from 'pounce';

export type TabsOrientation = 'horizontal' | 'vertical';
export type TabsVariant = 'contained' | 'underline' | 'outline' | 'text';
export type TabsSize = 'large' | 'small';

export interface TabsProps extends BoxProps {
  /**
   * The index of the tab that should be selected initially
   * @default 0
   */
  index?: number;

  /**
   * Callback fired when the tab selection changes
   */
  onChange?: (index: number) => void;

  /**
   * The orientation of the tabs
   * @default 'horizontal'
   */
  orientation?: TabsOrientation;

  /**
   * The size of the tabs (affects padding and text size of child tabs)
   * @default 'large'
   */
  size?: TabsSize;

  /**
   * The visual variant of the tabs
   * @default 'contained'
   */
  variant?: TabsVariant;

  /**
   * The children should be TabList and TabPanels components
   */
  children: React.ReactNode;
}

export interface TabsContextValue {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  orientation: TabsOrientation;
  size: TabsSize;
  variant: TabsVariant;
}

export const TabsContext = React.createContext<TabsContextValue | undefined>(undefined);

export const useTabsContext = (): TabsContextValue => {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('useTabsContext must be used within a Tabs component');
  }
  return context;
};

const Tabs: React.FC<TabsProps> = ({
  index = 0,
  onChange,
  orientation = 'horizontal',
  size = 'large',
  variant = 'contained',
  children,
  ...boxProps
}) => {
  const [selectedIndex, setSelectedIndex] = React.useState(index);

  // Update internal state when index prop changes
  React.useEffect(() => {
    setSelectedIndex(index);
  }, [index]);

  const handleChange = React.useCallback(
    (newIndex: number) => {
      setSelectedIndex(newIndex);
      onChange?.(newIndex);
    },
    [onChange],
  );

  const contextValue = React.useMemo(
    () => ({
      selectedIndex,
      setSelectedIndex: handleChange,
      orientation,
      size,
      variant,
    }),
    [selectedIndex, handleChange, orientation, size, variant],
  );

  return (
    <TabsContext.Provider value={contextValue}>
      <Box {...boxProps}>{children}</Box>
    </TabsContext.Provider>
  );
};

export default Tabs;
